import React, {
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  message,
  Typography,
} from 'antd';
import { PiPushPinFill } from 'react-icons/pi';

import CommunityAPI from '../../../../api/CommunityAPI';
import { updateCommunitPost } from '../../../../context/communityReducer';
import showAppError from '../../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  IPollVoteByUser,
  PollOption,
} from '../../../../types/communityMessagesTypes';
// import { useTheme } from '../../../../context/ThemeProvider';
import {
  IChannelPostDetails,
  IPostMediaItem,
} from '../../../../types/communityTypes';
import { ROUTES } from '../../../../types/routes';
import CreatePostMediaRender
  from '../../../CreateCommunityPost/components/CreatePostMediaRender/CreatePostMediaRender';
import ShoutoutPreview
  from '../../../UserProfile/components/ShoutoutsTab/ShoutoutPreview';
import PollVotedUserListModal from '../../components/PollVotedUserListModal';
import CommunityPostContent from './CommunityPostContent';
import CommunityPostHeader from './CommunityPostHeader';
import CommunityPostPoll from './CommunityPostPoll';
import CommunityPostStats from './CommunityPostStats';

type Props = {
  communityPostDetails: IChannelPostDetails;
  isRepost?: boolean;
};

interface IUserState {
  page: number;
  users: IPollVoteByUser[];
  loading: boolean;
  hasMore: boolean;
}

const CommunityPostItem: React.FC<Props> = ({
  communityPostDetails,
  isRepost = false,
}) => {
  const intialState: IUserState = useMemo(
    () => ({
      page: 1,
      users: [],
      loading: false,
      hasMore: true,
    }),
    [],
  );
  const dispatch = useAppDispatch();
  const { selectedChannelData } = useAppSelector((state) => state.community);
  const navigate = useAppNavigate();
  const { post, ...postDetails } = communityPostDetails;

  const [selectedOption, setSelectedOption] = useState('all');
  const [showPollVotedUserList, setShowPollVotedUserList] = useState(false);
  const [userState, setUserState] = useState(intialState);
  const [selectedTotalVotesCount, setSelectedTotalVotesCount] = useState(0);

  const channelId = useMemo(() => {
    return postDetails?.channel || selectedChannelData?._id;
  }, [postDetails?.channel, selectedChannelData?._id]);

  const fetchUserList = async () => {
    if (userState?.loading) return;
    if (!userState?.hasMore) return;
    try {
      setUserState((prev) => ({
        ...prev,
        loading: true,
      }));
      const query = new URLSearchParams();
      query.append('page', userState.page.toString());
      query.append('pageSize', '10');
      if (selectedOption !== 'all') {
        query.append('options', selectedOption);
      }
      const resp = await CommunityAPI.getPollVotesInPost(
        channelId as string,
        postDetails._id,
        query?.toString() || '',
      );
      if (resp.status === 200) {
        setUserState((prev) => ({
          ...prev,
          users: [...prev?.users, ...resp?.data?.result?.votes],
          loading: false,
          hasMore: resp?.data?.result?.votes?.length === 10,
          page: prev?.page + 1,
        }));
        setSelectedTotalVotesCount(resp?.data?.result?.totalVotes || 0);
      } else {
        showAppError(resp);
        setUserState((prev) => ({
          ...prev,
          loading: false,
          hasMore: false,
        }));
      }
    } catch (error) {
      showAppError(error);
      setUserState((prev) => ({
        ...prev,
        loading: false,
        hasMore: false,
      }));
    }
  };

  const undoVote = async (optionId?: string) => {
    if (!communityPostDetails?._id) return;
    if (!optionId) {
      message.warning('Something want wrong, please refresh and try again');
      return;
    }
    try {
      const resp = await CommunityAPI.removeVoteOnCommunityPoll(
        channelId as string,
        communityPostDetails?._id,
        { option: optionId },
      );
      if (resp?.status === 204) {
        const newVoteCountObj = { ...communityPostDetails?.pollVotesPerOption };
        if (newVoteCountObj?.[optionId]) {
          newVoteCountObj[optionId] = newVoteCountObj?.[optionId] - 1;
        } else {
          newVoteCountObj[optionId] = 0;
        }
        const newTotalVotes = (communityPostDetails?.totalPollVotes || 0) - 1;
        dispatch(
          updateCommunitPost({
            ...communityPostDetails,
            votedOption: false,
            pollVotesPerOption: newVoteCountObj,
            totalPollVotes: newTotalVotes,
          }),
        );
      } else {
        showAppError(resp?.data);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  const vote = async (optionId?: string) => {
    if (!communityPostDetails?._id) return;
    if (!optionId) {
      message.warning('Something want wrong, please refresh and try again');
      return;
    }
    if (communityPostDetails?.votedOption) {
      if (communityPostDetails?.post?.poll?.allowVoteChange) {
        message.info(
          'You alreay voted, but if you wish to change your vote click undo',
        );
        return;
      }
      message.info('You alreay voted');
      return;
    }
    try {
      const resp = await CommunityAPI.addVoteOnCommunityPoll(
        channelId as string,
        communityPostDetails?._id,
        { option: optionId },
      );
      if (resp?.status === 201) {
        const newVoteCountObj = { ...communityPostDetails?.pollVotesPerOption };
        if (newVoteCountObj?.[optionId]) {
          newVoteCountObj[optionId] = (newVoteCountObj?.[optionId] || 0) + 1;
        } else {
          newVoteCountObj[optionId] = 1;
        }
        const newTotalVotes = (communityPostDetails?.totalPollVotes || 0) + 1;
        dispatch(
          updateCommunitPost({
            ...communityPostDetails,
            votedOption: optionId,
            pollVotesPerOption: newVoteCountObj,
            totalPollVotes: newTotalVotes,
          }),
        );
      } else {
        showAppError(resp?.data);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  useEffect(() => {
    if (selectedOption) {
      setUserState(intialState);
    }
  }, [intialState, selectedOption]);

  return (
    <div
      data-post-id={postDetails._id} // Add this for IntersectionObserver
      data-post-creator={post?.postedBy?._id} // Add this for IntersectionObserver
      className="community-post-item_wrapper community-feed-post community-post-item"
      style={{ paddingBottom: 3 }}>
      {postDetails.isPinned && !isRepost ? (
        <div className="community-post-item_wrapper_pinned">
          <PiPushPinFill color="#000" size={15} />
          <Typography.Text>Pinned Post</Typography.Text>
        </div>
      ) : null}
      <CommunityPostHeader
        communityPostDetails={communityPostDetails}
        isRepost={isRepost}
      />
      {post.caption && !post?.poll ? (
        <CommunityPostContent
          caption={post.caption}
          maxLines={post.mediaContent?.length ? 2 : 5}
          onCaptionPressed={() => {}}
          key={post?._id}
        />
      ) : null}
      {post?.mediaContent?.length ? (
        <CreatePostMediaRender
          postedBy={post?.postedBy?._id}
          files={post?.mediaContent.map(
            (file) =>
              ({
                ...file,
                type: file?.contentType,
                url: file?.contentUrl,
                name: file?.fileName,
                postedBy: post?.postedBy?._id,
                postId: postDetails?._id,
              }) as IPostMediaItem,
          )}
          selectedLayout={post?.layoutType}
        />
      ) : null}
      {post?.postRef ? (
        <div className="creatorPostsWrapper__repost-card">
          <CommunityPostItem
            key={post?.postRef?._id}
            // index={0}
            communityPostDetails={
              { post: post?.postRef } as IChannelPostDetails
            }
            isRepost={Boolean(post?.postRef?._id)}
          />
        </div>
      ) : null}
      {post?.poll ? (
        <CommunityPostPoll
          vote={vote}
          undoVote={undoVote}
          poll={post?.poll}
          postedBy={post?.postedBy}
          pollVotesPerOption={communityPostDetails?.pollVotesPerOption || {}}
          totalPollVotes={postDetails?.totalPollVotes as number}
          votedOption={postDetails?.votedOption as string | false}
          setSelectedOption={setSelectedOption}
          setShowPollVotedUserList={setShowPollVotedUserList}
        />
      ) : null}

      {post.tqConnectShoutout && post.tqConnectShoutout._id ? (
        <ShoutoutPreview
          coverImage={post.tqConnectShoutout.coverImage}
          category={post.tqConnectShoutout.category}
          description={post.tqConnectShoutout.description}
        />
      ) : null}

      {isRepost ? null : (
        <CommunityPostStats
          postId={post?._id}
          communityPostDetails={communityPostDetails}
          onRepostPressed={() =>
            navigate(
              ROUTES.COMMUNITY_CREATE_POST,
              {
                postRefId:
                  communityPostDetails?.post?.postRef?._id ||
                  communityPostDetails._id,
                channelId: channelId as string,
              }, // If reposted post is getting reposted, then postRefId and postRefDetails will be the of original post which is being reposted in the first place
              {
                state: {
                  postRefDetails:
                    communityPostDetails?.post?.postRef || communityPostDetails,
                },
              },
            )
          }
        />
      )}
      <PollVotedUserListModal
        showModal={showPollVotedUserList}
        closeModal={() => {
          setShowPollVotedUserList(false);
        }}
        question={communityPostDetails?.post?.poll?.question as string}
        pollOptions={
          communityPostDetails?.post?.poll?.pollOptions as PollOption[]
        }
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        userList={userState?.users}
        totalPollVotes={selectedTotalVotesCount}
        hasMore={userState?.hasMore}
        fetchUserList={fetchUserList}
      />
    </div>
  );
};

export default memo(CommunityPostItem);
